/**
 * THE THREAD'S LAYOUT RULES (ITP-E-203-06), after carbon's `groupMessages` and run rules (OCA-E-160 `ChatPanel.tsx`
 * 518-598, 4376-4741): where a day is stated, whose runs are named, where read faces belong, and who placed a call row
 * (ITP-E-240-07). Pure, so the thread only draws what these decide.
 */

const pad = (value: number): string => String(value).padStart(2, '0');

const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * The day stated above each message, or null. A thread that is entirely today states none. The first message is
 * labelled only when it is not today's; after that a label goes wherever the day moved, so once a second day is in
 * the thread every day's first run carries its date, today's included. `dd/mm/yy`, the list row's date.
 */
export function daySeparators(messages: readonly { createdDate?: string | Date | null }[] | null | undefined,
                              now: Date = new Date()): (string | null)[] {
  let previous: Date | null = null;
  return (messages || []).map(message => {
    const at = message?.createdDate ? new Date(message.createdDate) : null;
    if (!at || isNaN(at.getTime())) {
      return null;
    }
    const opens = previous === null && !sameDay(at, now);
    const moved = previous !== null && !sameDay(at, previous);
    previous = at;
    return opens || moved ? `${pad(at.getDate())}/${pad(at.getMonth() + 1)}/${pad(at.getFullYear() % 100)}` : null;
  });
}

/**
 * A run's sender line: in a group, over every run but the reader's own; and always over XatBot's, the one sender a
 * 1:1's header never names.
 */
export function showSender(run: { mine: boolean, isXatBot?: boolean }, isGroup: boolean): boolean {
  return !!run.isXatBot || (isGroup && !run.mine);
}

/** The Assistants' authors (ADR 0177): XatBot and Sara, by email. */
const ASSISTANT_EMAILS = ['xatbot@orbitax.com', 'sara@orbitax.com'];

/** Whether `createdBy` is an Assistant: its sender line shows in a 1:1 too. */
export function isAssistantAuthor(createdBy: { email?: string, firstName?: string } | null | undefined): boolean {
  return ASSISTANT_EMAILS.includes((createdBy?.email || '').toLowerCase()) || createdBy?.firstName === 'XatBot';
}

/**
 * A run's sender line: the author's name and, on an Assistant's answer in a chat (MOBILE-E-104-03), who asked it —
 * "XatBot · asked by Omar Harness", or "asked by you" for the reader.
 */
export function senderLine(createdBy: { email?: string, firstName?: string, lastName?: string } | null | undefined,
                           askedBy: { email?: string, name?: string } | null | undefined, me: string | null | undefined): string {
  const name = [createdBy?.firstName, createdBy?.lastName].filter(Boolean).join(' ');
  if (!askedBy?.email) {
    return name;
  }
  const asker = askedBy.email.toLowerCase() === (me || '').toLowerCase() ? 'you' : (askedBy.name || askedBy.email);
  return `${name} · asked by ${asker}`;
}

/** Read faces under a message belong to a group; in a 1:1 the read tick already says it. */
export function showReadFaces(isGroup: boolean): boolean {
  return isGroup;
}

/** What a call row says about its caller: the chat query's call fields and the row's author (D-18, `CallCardMessage`). */
export interface CallRowCallerFields {
  isMine?: boolean;
  callOutcome?: string;
  callerName?: string;
  callerEmail?: string;
  createdBy?: {email?: string, firstName?: string, lastName?: string};
}

/**
 * Who placed a call row, said above it as a message's sender line says who wrote it (ITP-E-240-07, Q23): "You" on the
 * reader's own (the row is theirs, or the server says they were its caller, as the call card decides), else the caller's
 * name, else the author's first and last name, else an email; null when the row names nobody. Not tied to the chat being
 * a group: a call card has no other line that says who called.
 */
export function callRowCaller(row: CallRowCallerFields | null | undefined): string | null {
  if (!row) {
    return null;
  }
  if (row.isMine === true || row.callOutcome === 'Caller') {
    return 'You';
  }
  const author = row.createdBy;
  const authorName = `${author?.firstName || ''} ${author?.lastName || ''}`.trim();
  return row.callerName?.trim() || authorName || author?.email || row.callerEmail || null;
}
